import type { ApacCandidateHeader } from '@seek/apac-candidate-header-footer';
import { compatibility } from '@seek/ca-myprofile-utils';
import { useMelwaysInfo } from '@seek/melways-react';
import { metrics } from '@seek/metrics-js';
import { oauth } from '@seek/oauth-url-builders';
import { Page, PageBlock, Stack } from 'braid-design-system';
import { type ComponentProps, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { useSetTrackingState } from 'src/modules/analytics/analytics';
import { useGetProfileAvatar } from 'src/modules/graphql/avatar/data';
import { useTypedSelector } from 'src/reducers';
import { getUser, updateUserInfo } from 'src/reducers/user/actionCreators';
import { getLanguagePrefixedPath } from 'src/utils/getLanguagePrefixedPath';
import { pathShouldNotRedirectToSignIn } from 'src/utils/pathShouldNotRedirectToSignIn';

import Footer from '../Footer/Footer';
import Header from '../Header/Header';
import { Settings } from '../Settings/Settings';

export type ApacHeaderFooterAuthenticationStatus = ComponentProps<
  typeof ApacCandidateHeader
>['authenticationStatus'];

export const App = () => {
  const { language: melwaysLanguage } = useMelwaysInfo();
  const { profileAvatar } = useGetProfileAvatar();
  const language =
    compatibility.getCompatibleLanguageFromLocaleString(melwaysLanguage);

  const firstName = useTypedSelector((state) => state.user.firstName);
  const lastName = useTypedSelector((state) => state.user.lastName);
  const email = useTypedSelector((state) => state.user.emailAddress);
  const country = useTypedSelector(
    (state) => state.page.pageAnalytics.siteCountry,
  );
  const brand = useTypedSelector((state) => state.page.pageAnalytics.brand);

  const authenticationStatus = useTypedSelector(
    (state) => state.user.authenticationStatus,
  );

  const dispatch = useDispatch();

  // Similar to componentDidMount and componentDidUpdate:
  useEffect(() => {
    const getUserApi = async () => {
      if (authenticationStatus === 'pending') {
        // Update the document title using the browser API
        const userData: any = await dispatch(getUser(language, country, brand));
        const userDetails = {
          loginId: userData?.id,
          isLoggedIn: true,
          profileGuid: userData?.emailCorrelationId,
        };
        dispatch(
          updateUserInfo(
            userDetails.loginId,
            userDetails.isLoggedIn,
            userDetails.profileGuid,
          ),
        );
      }
    };
    getUserApi();
  });

  useEffect(() => {
    if (authenticationStatus === 'authenticated') {
      metrics.sendInitialLoadTime('settings');
    }
  }, [authenticationStatus]);

  if (authenticationStatus === 'unauthenticated') {
    const nextStepUrl = `${oauth.buildLoginUrl({
      location: {
        origin: '',
        pathname: getLanguagePrefixedPath(language, '/settings'),
        search: '',
      },
      language,
    })}`;
    if (
      typeof window !== 'undefined' &&
      !pathShouldNotRedirectToSignIn(window.location.pathname, language)
    ) {
      window.location.assign(nextStepUrl);
    }
  }

  useSetTrackingState(); // Sets up tracking props

  return (
    <Page
      footer={
        <Footer
          authenticationStatus={
            authenticationStatus as ApacHeaderFooterAuthenticationStatus
          }
        />
      }
      footerPosition="belowFold"
    >
      <Stack space="xlarge">
        <Header
          authenticationStatus={
            authenticationStatus as ApacHeaderFooterAuthenticationStatus
          }
          userProfile={{
            firstName,
            lastName,
            email,
            profilePhotoUrl: profileAvatar?.image128Url ?? undefined,
          }}
        />
        <PageBlock width="small">
          <Settings />
        </PageBlock>
      </Stack>
    </Page>
  );
};
